'use client';

import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Check } from 'lucide-react';
import { ForfaitSelector, ForfaitOption } from './ForfaitSelector';

interface PackStandardVirtuelCardProps {
  isAnnual: boolean;
}

// Données des forfaits Pack Standard Virtuel
const FORFAITS: ForfaitOption[] = [
  { id: '1h', label: '1h/mois', priceMonthly: null, priceAnnual: 49 },
  { id: '6h', label: '6h/mois', priceMonthly: 9.9, priceAnnual: 99 },
  { id: '10h', label: '10h/mois', priceMonthly: 14.9, priceAnnual: 149 },
  { id: '30h', label: '30h/mois', priceMonthly: 29.9, priceAnnual: 299 },
];

// Features avec placeholder pour la valeur dynamique
const getFeatures = (forfaitLabel: string) => [
  '1 Numéro fixe France (01, 02, 03, 04, 05, 09 et DOM-TOM) ou conservez le vôtre avec la portabilité',
  `<strong>${forfaitLabel}</strong> de redirections d'appels vers un nombre illimité de lignes fixes et mobiles en France Métropolitaine & DOM-TOM`,
  'Abonnement sans engagement',
  'Standard Virtuel Interactif avec toutes les fonctionnalités incluses (Message d\'accueil, musique d\'attente, renvoi d\'appels en cascade ou en simultané, configuration des horaires, etc)',
  'Journal d\'appel CRM avec tags d\'appels',
  'Espace client en ligne web & mobile',
  'Activation instantanée 100% en ligne',
];

export function PackStandardVirtuelCard({ isAnnual }: PackStandardVirtuelCardProps) {
  const router = useRouter();
  
  // Sélectionner le premier forfait disponible par défaut
  const defaultForfait = isAnnual ? '6h' : '6h';
  const [selectedForfait, setSelectedForfait] = useState(defaultForfait);

  // Récupérer le forfait sélectionné
  const currentForfait = useMemo(() => {
    return FORFAITS.find((f) => f.id === selectedForfait) || FORFAITS[1];
  }, [selectedForfait]);

  // Prix actuel
  const currentPrice = isAnnual ? currentForfait.priceAnnual : currentForfait.priceMonthly;

  // Features avec la valeur dynamique
  const features = useMemo(() => {
    return getFeatures(currentForfait.label);
  }, [currentForfait]);

  const handleSubscribe = () => {
    router.push(`/commande?type=commande`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-primaryGreen overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="bg-primaryGreen text-white p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <img
              src="/pack-std.png"
              alt="Pack Standard Virtuel"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            <h3 className="text-xl font-bold">Pack Standard Virtuel</h3>
            <p className="text-sm opacity-90">Je démarre</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col">
        {/* Sélecteur de forfait */}
        <div className="mb-6 text-center">
          <p className="text-sm text-gray-600 mb-3">Choisissez votre forfait :</p>
          <ForfaitSelector
            options={FORFAITS}
            selectedId={selectedForfait}
            onSelect={setSelectedForfait}
            isAnnual={isAnnual}
            colorScheme="green"
          />
        </div>

        {/* Prix */}
        <div className="mb-6 text-center">
          {currentPrice !== null ? (
            <>
              <p className="text-4xl font-bold text-primaryGreen">
                {currentPrice.toFixed(2).replace('.', ',')}€
                <span className="text-lg font-normal text-gray-600">
                  {isAnnual ? ' HT/an' : ' HT/mois'}
                </span>
              </p>
            </>
          ) : (
            <p className="text-gray-500">Non disponible en mensuel</p>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-6 flex-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primaryGreen flex-shrink-0 mt-0.5" />
              <span
                className="text-sm text-gray-700 text-left"
                dangerouslySetInnerHTML={{ __html: feature }}
              />
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button
          onClick={handleSubscribe}
          disabled={currentPrice === null}
          className="w-full py-3 px-6 bg-primaryGreen text-white font-semibold rounded-full hover:bg-primaryGreen/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Souscrire
        </button>
      </div>
    </div>
  );
}
